import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube  ,Whatsapp} from "lucide-react";
import { FaWhatsapp } from 'react-icons/fa';
import styles from "./style.module.scss";

import { motion } from "framer-motion";

const Footer = () => {
  const { t } = useTranslation("footer");
  const data = t("data", { returnObjects: true });
  const navigate = useNavigate();
  const touchTimer = useRef(null);

  // Keyboard shortcut logic for desktop
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        navigate('/admin/login');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  // Long press logic for mobile
  const handleTouchStart = () => {
    touchTimer.current = setTimeout(() => {
      navigate('/admin/login');
    }, 5000);
  };

  const handleTouchEndOrMove = () => {
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
      touchTimer.current = null;
    }
  };

  if (!data) return null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <footer className={styles.footer}>
      <div className={styles.topSection}>
        <div className={styles.staticBackground}>
          <img src="/assets/images/footer-bg.png" alt="Map Background" />
        </div>

        <div className={styles.content}>
          <motion.div 
            className={styles.contactInfo}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className={styles.contactTitle}>{data.contact?.title}</h2>
            <motion.div 
              className={styles.infoWrapper}
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div className={styles.contactItem} variants={itemVariants}>
                <MapPin size={20} className={styles.icon} />
                <span>{data.contact?.address}</span>
              </motion.div>
              {data.contact?.phones?.map((phone, index) => (
                <motion.div key={index} className={styles.contactItem} variants={itemVariants}>
                  <Phone size={20} className={styles.icon} />
                  <a href={`tel:${phone.replace(/\s/g, "")}`}>{phone}</a>
                </motion.div>
              ))}
              <motion.div className={styles.contactItem} variants={itemVariants}>
                <Mail size={20} className={styles.icon} />
                <a href={`mailto:${data.contact?.email}`}>{data.contact?.email}</a>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.navSection}>
          <motion.ul 
            className={styles.linksGrid}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            {data.links?.map((link, index) => (
              <motion.li key={index} variants={itemVariants}>
                <Link to={link.to}>{link.name}</Link>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        <div className={styles.bottomSection}>
          <div className={styles.socialAndCopyright}>
            <motion.div 
              className={styles.socialLinks}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <motion.a whileHover={{ y: -5 }} href="https://www.facebook.com/roland.sharyan.2025?locale=ru_RU" target="_blank" rel="noopener noreferrer" className={styles.socialIcon}>
                <Facebook size={24} />
              </motion.a>
              <motion.a whileHover={{ y: -5 }} href="https://www.instagram.com/best_holiday_armenia/" target="_blank" rel="noopener noreferrer" className={styles.socialIcon}>
                <Instagram size={24} />
              </motion.a>
              <motion.a whileHover={{ y: -5 }} href="https://wa.me/37477271066" target="_blank" rel="noopener noreferrer" className={styles.socialIcon}>
                <FaWhatsapp size={24} />
              </motion.a>
            </motion.div>
              <div className={styles.lowerLinks}>
                <p 
                  className={styles.copyright}
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEndOrMove}
                  onTouchMove={handleTouchEndOrMove}
                >
                  {data.copyright}
                </p>
                <div className={styles.utilityLinks}>
                  <Link to="/sitemap" className={styles.sitemapLink}>{data.sitemap}</Link>
                </div>
              </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
